import { useState, useEffect, useCallback } from 'react';
import Helmet from 'react-helmet';
import {
  Box,
  Container,
  Grid,
  Title,
  Text,
  TextInput,
  Group,
  MultiSelect,
  Checkbox,
  Pagination,
  Badge,
  MantineProvider,
  ActionIcon,
  Drawer,
  Button,
  Stack,
  Divider,
  Loader,
} from '@mantine/core';
import { Head, router } from '@inertiajs/react';
import { IconSearch, IconAdjustmentsHorizontal } from '@tabler/icons-react';
import DynamicDrawer from '../../components/Drawer/DynamicDrawer';
import { StablePagination } from '../../components/StablePagination';
import '../../styles/ShowView.css';

import { ArtistCard } from '../../components/ui';

interface DanceStyle {
  id: number;
  name: string;
}

interface Artist {
  id: number;
  name: string;
  slug: string;
  avatar_url?: string;
  instructor: boolean;
  dj: boolean;
  dance_styles?: DanceStyle[];
  dance_style_ids?: number[];
  description?: string | { body: string };
  [key: string]: any;
}

interface PaginationData {
  current_page: number;
  total_pages: number;
  total_count: number;
  per_page: number;
}

interface FiltersData {
  search?: string;
  dance_styles?: string;
  instructor: boolean;
  dj: boolean;
}

interface ArtistIndexProps {
  artists: Artist[];
  totalArtists: number;
  danceStyles: DanceStyle[];
  pagination: PaginationData;
  filters: FiltersData;
  page_meta?: {
    title: string;
    description: string;
    url: string;
    image: string;
  };
}

// Move debounce function outside component to prevent recreation
function debounce(func: Function, delay: number) {
  let timeoutId: NodeJS.Timeout;
  return function (...args: any[]) {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => {
      func.apply(this, args);
    }, delay);
  };
}

export default function ArtistIndex({
  artists,
  totalArtists,
  danceStyles,
  pagination,
  filters,
  page_meta,
}: ArtistIndexProps) {
  // State for search and filtering
  const [searchTerm, setSearchTerm] = useState(filters.search || '');
  const [selectedDanceStyles, setSelectedDanceStyles] = useState<string[]>(
    filters.dance_styles ? filters.dance_styles.split(',') : [],
  );
  const [isInstructor, setIsInstructor] = useState(filters.instructor || false);
  const [isDj, setIsDj] = useState(filters.dj || false);
  const [isLoading, setIsLoading] = useState(false);

  // State for drawer
  const [filterDrawerOpened, setFilterDrawerOpened] = useState(false);

  // State for pagination
  const [currentPage, setCurrentPage] = useState(pagination.current_page);

  // State for dynamic drawer
  const [artistDrawerOpened, setArtistDrawerOpened] = useState(false);
  const [selectedArtist, setSelectedArtist] = useState<Artist | null>(null);

  // State for dynamic count
  const [filteredCount, setFilteredCount] = useState(pagination.total_count);
  const [isCountLoading, setIsCountLoading] = useState(false);

  // Calculate active filter count
  const activeFilterCount = selectedDanceStyles.length + (isInstructor ? 1 : 0) + (isDj ? 1 : 0);

  // Function to load artists with current filters and pagination
  const loadArtists = (page: number = 1) => {
    setIsLoading(true);

    // Build query parameters
    const params: Record<string, any> = { page: page };

    if (searchTerm) params.search = searchTerm;
    if (selectedDanceStyles.length > 0) params.dance_styles = selectedDanceStyles.join(',');
    if (isInstructor) params.instructor = 'true';
    if (isDj) params.dj = 'true';

    // Navigate with Inertia
    router.visit('/artists', {
      data: params,
      preserveState: true,
      onSuccess: () => {
        setIsLoading(false);
      },
      onError: () => {
        setIsLoading(false);
      },
    });
  };

  // Debounced function to fetch filtered count
  const fetchFilteredCount = useCallback(
    debounce(async (searchTerm: string, danceStyles: string[], instructor: boolean, dj: boolean) => {
      setIsCountLoading(true);

      try {
        // Build query parameters for the API call
        const params = new URLSearchParams();
        if (searchTerm) params.append('search', searchTerm);
        if (danceStyles.length > 0) params.append('dance_styles', danceStyles.join(','));
        if (instructor) params.append('instructor', 'true');
        if (dj) params.append('dj', 'true');

        // Make API call to get count
        const response = await fetch(`/artists.json?${params.toString()}`, {
          headers: {
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
        });

        if (response.ok) {
          const data = await response.json();
          setFilteredCount(data.pagination.total_count);
        }
      } catch (error) {
        console.error('Error fetching filtered count:', error);
      } finally {
        setIsCountLoading(false);
      }
    }, 300), // 300ms debounce
    [], // Empty dependency array since debounce is now stable
  );

  // Update filtered count when pagination changes (after applying filters)
  useEffect(() => {
    setFilteredCount(pagination.total_count);
  }, [pagination.total_count]);

  // Trigger count fetch when filters change
  useEffect(() => {
    fetchFilteredCount(searchTerm, selectedDanceStyles, isInstructor, isDj);
  }, [searchTerm, selectedDanceStyles, isInstructor, isDj, fetchFilteredCount]);

  useEffect(() => {
    // If you need to manually update the document title and meta tags
    if (page_meta?.title) {
      document.title = page_meta.title;
    }
    // console.log('🤓 Artist Index Page meta:', page_meta);
    // Optional: Update meta description directly if Helmet doesn't work
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription && page_meta?.description) {
      metaDescription.setAttribute('content', page_meta.description);
    }
  }, [page_meta]);

  // Debounced search function
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchTerm !== filters.search) {
        loadArtists(1); // Reset to first page on search change
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Update currentPage when pagination changes from props
  useEffect(() => {
    setCurrentPage(pagination.current_page);
  }, [pagination.current_page]);

  // Handle page change
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    loadArtists(page);
  };

  // Handle artist card click
  const handleArtistClick = (artist: Artist) => {
    setSelectedArtist(artist);
    setArtistDrawerOpened(true);
  };

  // Handle artist drawer close
  const handleArtistDrawerClose = () => {
    setArtistDrawerOpened(false);
  };

  // Handle apply filters
  const handleApplyFilters = () => {
    loadArtists(1); // Reset to first page on filter change
    setFilterDrawerOpened(false);
  };

  // Handle clear all filters
  const handleClearFilters = () => {
    setSelectedDanceStyles([]);
    setIsInstructor(false);
    setIsDj(false);
  };

  // Format dance styles for MultiSelect
  const danceStyleOptions = danceStyles.map((style) => ({
    value: style.id.toString(),
    label: style.name,
  }));

  return (
    <>
      <MantineProvider>
        {page_meta && (
          <Helmet>
            <title>{page_meta.title}</title>
            <meta name="description" content={page_meta.description} />

            {/* Open Graph tags */}
            <meta property="og:title" content={page_meta.title} />
            <meta property="og:description" content={page_meta.description} />
            <meta property="og:type" content="website" />
            <meta property="og:url" content={page_meta.url} />
            <meta property="og:image" content={page_meta.image} />

            {/* Twitter Card tags */}
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={page_meta.title} />
            <meta name="twitter:description" content={page_meta.description} />
            <meta name="twitter:image" content={page_meta.image} />
          </Helmet>
        )}
        <Head title="Artists" />

        <Container size="md" mt="0" mb="8rem" pos="relative">
          {/* Sticky Search Section */}

          <Box
            className="filter-wrapper"
            style={{
              position: 'sticky',
              top: '8px',
              zIndex: 100,
              backgroundColor: '#75757550',
              padding: '16px',
              marginBottom: '1.5rem',
              borderRadius: '12px',
              margin: '12px 8px 1.5rem 0px',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4), 0 2px 8px rgba(0, 0, 0, 0.2)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              backdropFilter: 'blur(8px)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', width: '100%' }}>
              <TextInput
                leftSection={<IconSearch size={14} />}
                placeholder="Search artists..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.currentTarget.value)}
                style={{ flex: 1, minWidth: 0 }} // flex: 1 takes remaining space, minWidth: 0 allows shrinking
              />

              {/* Filter Action Icon with Badge */}
              <Box pos="relative" style={{ flexShrink: 0 }}>
                {' '}
                {/* flexShrink: 0 prevents icon from shrinking */}
                <ActionIcon variant="filled" size="xl" color="#EA580C" onClick={() => setFilterDrawerOpened(true)}>
                  <IconAdjustmentsHorizontal />
                </ActionIcon>
                {activeFilterCount > 0 && (
                  <Badge
                    color="orange"
                    size="ms"
                    radius="xl"
                    style={{
                      position: 'absolute',
                      top: -15,
                      right: -10,
                      padding: '4px 8px',
                      minWidth: '24px',
                      height: '24px',
                    }}
                  >
                    {activeFilterCount}
                  </Badge>
                )}
              </Box>
            </div>
          </Box>
          {/* Results Section */}
          <Box className="results-wrapper">
            <Text className="poppins mb-2" c="#f2f2f2">
              Showing {artists.length} of {pagination.total_count} artists.
              {isLoading && <Loader color="orange" size="xs" ml="md" />}
            </Text>

            {/* Pagination */}
            {pagination.total_pages > 1 && (
              <Group justify="flex-start" mb="md">
                <StablePagination
                  total={pagination.total_pages}
                  value={currentPage}
                  onChange={handlePageChange}
                  color="orange"
                />
              </Group>
            )}

            {/* Artist Grid */}
            <Grid gutter="md">
              {artists.map((artist) => (
                <Grid.Col span={{ base: 6, xs: 4, sm: 3, md: 3, lg: 3 }} key={artist.id}>
                  <ArtistCard artist={artist} onCardClick={handleArtistClick} />
                </Grid.Col>
              ))}
            </Grid>

            {/* Show message if no results */}
            {artists.length === 0 && (
              <Text ta="center" fz="lg" fw={500} mt="xl">
                No artists found matching your search criteria.
              </Text>
            )}

            {/* Bottom Pagination */}
            {pagination.total_pages > 1 && (
              <Group justify="flex-start" mt="md">
                <StablePagination
                  total={pagination.total_pages}
                  value={currentPage}
                  onChange={handlePageChange}
                  color="orange"
                />
              </Group>
            )}
          </Box>
        </Container>

        {/* Filter Drawer from bottom */}
        <Drawer
          opened={filterDrawerOpened}
          onClose={() => setFilterDrawerOpened(false)}
          position="right"
          size="md"
          withCloseButton={true}
          style={{ zIndex: 1000 }}
          styles={{
            root: { zIndex: 1000 },
            header: {
              backgroundColor: '#18181B',
              color: '#f2f2f2',
              borderBottom: '1px solid #333',
            },
            body: {
              backgroundColor: '#18181B',
              color: '#f2f2f2',
            },
            close: {
              color: '#f2f2f2',
              '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
            },
          }}
        >
          <Stack gap="0" p="md" style={{ width: '100%', minHeight: '100vh' }}>
            <Text size="lg" fw={600} c="#f2f2f2" style={{ lineHeight: 1 }}>
              Filters
            </Text>

            <Divider mb="md" color="#333" className="mt-2" />

            {/* Dance Styles Filter */}
            <Box className="mb-4">
              <Text fw={500} className="mb-2" c="#f2f2f2">
                Dance Styles
              </Text>
              <MultiSelect
                data={danceStyleOptions}
                placeholder={selectedDanceStyles.length === 0 ? 'Select dance styles' : ''}
                value={selectedDanceStyles}
                onChange={setSelectedDanceStyles}
                clearable
                searchable
                hidePickedOptions
                variant="filled"
                size="md"
                w="100%"
                className="custom-select"
              />
            </Box>

            {/* Artist Type Filters */}
            <Box>
              <Text fw={500} mb="xs" c="#f2f2f2" className="poppins">
                Artist Type
              </Text>
              <Group mt="xs">
                <Checkbox
                  label="Instructors"
                  checked={isInstructor}
                  color="#EA580C"
                  onChange={(e) => setIsInstructor(e.currentTarget.checked)}
                  styles={{
                    label: { color: '#f2f2f2' },
                    input: {
                      '&:checked': {
                        backgroundColor: '#EA580C',
                        borderColor: '#EA580C',
                      },
                    },
                  }}
                />
                <Checkbox
                  label="DJs"
                  checked={isDj}
                  color="#EA580C"
                  onChange={(e) => setIsDj(e.currentTarget.checked)}
                  styles={{
                    label: { color: '#f2f2f2' },
                    input: {
                      '&:checked': {
                        backgroundColor: '#EA580C',
                        borderColor: '#EA580C',
                      },
                    },
                  }}
                />
              </Group>
            </Box>

            <Divider my="md" color="#333" className="mb-5" />

            {/* Bottom Action Row with Count and Clear Filters */}
            <Group justify="start" mt="lg">
              <Button
                variant="light"
                color="orange"
                size="sm"
                className="clear-button"
                onClick={handleClearFilters}
                disabled={activeFilterCount === 0}
                styles={{
                  root: {
                    borderColor: '#EA580C10',
                    '&:disabled': {
                      borderColor: '#3F3F46',
                      color: '#71717A',
                    },
                  },
                }}
              >
                Clear All Filters
              </Button>
              <Button
                variant="filled"
                color="#EA580C"
                radius="sm"
                size="md"
                onClick={handleApplyFilters}
                disabled={isCountLoading}
              >
                {isCountLoading ? (
                  <>
                    <Loader size="xs" color="white" mr="xs" />
                    Loading...
                  </>
                ) : (
                  `Show ${filteredCount} Artist${filteredCount !== 1 ? 's' : ''}`
                )}
              </Button>
            </Group>
          </Stack>
        </Drawer>

        {/* Artist Dynamic Drawer */}
        {selectedArtist && (
          <DynamicDrawer
            opened={artistDrawerOpened}
            type="artist"
            data={selectedArtist}
            onClose={handleArtistDrawerClose}
          />
        )}
      </MantineProvider>
    </>
  );
}
